import React, { useEffect, useState } from 'react';
import _, { get, sumBy } from 'lodash';
import Summary from 'components/UI/Summary';
import currencyFormat from 'utils/currency-format';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { loadNumberOfBookings, loadSales } from 'store/dispatchers/memberReports.dispatchers';
import { buildReportsSelector } from 'store/selectors/memberReports.selector';

function MemberReports({
    getSales, getNumberOfBookings, storedSalesReport, storedBookingsReport, isB2cPage,
})
{
    const [salesReport, setSalesReport] = useState({
        currentMonthSales: '',
        currentMonthCommission: '',
        salesPercentageDiff: '',
        commissionPercentageDiff: '',
        commissionBalance: 0,
        salesBalance: 0,
    });

    const [bookingReport, setBookingReport] = useState({
        currentYearSalesAvg: '',
        currentYearBookings: '',
        salesAvgPercentageDiff: '',
        bookingsPercentageDiff: '',
        bookingsBalance: 0,
        salesAvgBalance: 0,
    });

    const fillSalesReport = (salesData) =>
    {
        const lastYearSales = get(salesData, '0.sales', 0);
        const lastYearCommission = get(salesData, '0.member_commission', 0);
        const currentMonthSales = get(salesData, '1.sales', 0);
        const currentMonthCommission = get(salesData, '1.member_commission', 0);

        const salesDiff = currentMonthSales - lastYearSales;
        const commissionDiff = currentMonthCommission - lastYearCommission;

        setSalesReport({
            currentMonthSales,
            currentMonthCommission,
            salesPercentageDiff: (salesDiff / lastYearSales) * 100,
            commissionPercentageDiff: (commissionDiff / lastYearCommission) * 100,
            commissionBalance: commissionDiff,
            salesBalance: salesDiff,
        });
    };
    const fillBookingsReport = (bookingData) =>
    {
        const lastYearData = get(bookingData, '0', []) || [];
        const currentYearData = get(bookingData, '1', []) || [];

        const lastYearSalesAvg = sumBy(lastYearData, 'gross') / lastYearData.length;
        const currentYearSalesAvg = sumBy(currentYearData, 'gross') / currentYearData.length;

        const salesDiff = currentYearSalesAvg - lastYearSalesAvg;
        const bookingsDiff = currentYearData.length - lastYearData.length;

        setBookingReport({
            currentYearSalesAvg,
            currentYearBookings: currentYearData.length.toString(),
            salesAvgPercentageDiff: lastYearSalesAvg ? (salesDiff / lastYearSalesAvg) * 100 : 0,
            bookingsPercentageDiff: lastYearData.length ? (bookingsDiff / lastYearData.length) * 100 : 0,
            bookingsBalance: bookingsDiff,
            salesAvgBalance: salesDiff,
        });
    };

    const loadReportBookings = () =>
    {
        if (!_.isEmpty(storedBookingsReport))
        {
            fillBookingsReport(storedBookingsReport.data);
            return;
        }

        getNumberOfBookings();
    };

    const loadReportSales = () =>
    {
        if (!_.isEmpty(storedSalesReport))
        {
            fillSalesReport(storedSalesReport.data);
            return;
        }

        getSales();
    };

    useEffect(() =>
    {
        if (!isB2cPage)
        {
            loadReportSales();
        }
    }, [storedSalesReport]);

    useEffect(() =>
    {
        loadReportBookings();
    }, [storedBookingsReport]);

    return !isB2cPage && (
        <>
            <Summary
                title="Sales"
                subtitle="Year-to-Date"
                compareTitle="Compared Year-over-Year"
                isLoading={salesReport.currentMonthSales === ''}
                total={currencyFormat(salesReport.currentMonthSales)}
                compareTotal={`${currencyFormat(salesReport.salesPercentageDiff, 2, null)}%`}
                balance={salesReport.salesBalance}
            />
            <Summary
                title="Avg. Sales Size"
                subtitle="Year-to-Date"
                compareTitle="Compared Year-over-Year"
                isLoading={bookingReport.currentYearSalesAvg === ''}
                total={currencyFormat(bookingReport.currentYearSalesAvg)}
                compareTotal={`${currencyFormat(bookingReport.salesAvgPercentageDiff, 2, null)}%`}
                balance={bookingReport.salesAvgBalance}
            />
            <Summary
                title="Commission"
                subtitle="Year-to-Date"
                compareTitle="Compared Year-over-Year"
                isLoading={salesReport.currentMonthCommission === ''}
                compareTotal={`${currencyFormat(salesReport.commissionPercentageDiff, 2, null)}%`}
                total={currencyFormat(salesReport.currentMonthCommission)}
                balance={salesReport.commissionBalance}
            />
        </>
    );
}

MemberReports.defaultProps = {
    isB2cPage: false,
};

MemberReports.propTypes = {
    isB2cPage: PropTypes.bool,
    getSales: PropTypes.func,
    getNumberOfBookings: PropTypes.func,
    storedSalesReport: PropTypes.object,
    storedBookingsReport: PropTypes.object,
};

const mapStateToProps = () =>
{
    const reportSelector = buildReportsSelector();

    return (state) => ({
        storedSalesReport: reportSelector(state, 'sales_report'),
        storedBookingsReport: reportSelector(state, 'bookings_report'),
    });
};

const mapDispatchToProps = (dispatch) => ({
    getSales()
    {
        return dispatch(loadSales());
    },
    getNumberOfBookings()
    {
        return dispatch(loadNumberOfBookings());
    },
});

export default connect(mapStateToProps, mapDispatchToProps)(MemberReports);
